import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useHistory } from 'react-router';
import { makeStyles } from 'tss-react/mui';

import { Chip } from '@mui/material';

import useStores from 'hooks/use-stores';
import { SearchType } from 'main/components/SearchBar';

const useStyles = makeStyles()({
    root: {
        margin: '0 auto',
        width: '100%',
        marginBottom: 16
    },
    chips: {
        display: 'flex',
        justifyContent: 'flex-start',
        flexWrap: 'wrap',
        paddingBottom: 30,
        '@media (max-width: 960px)': {
            display: 'flex',
            flexDirection: 'row',
            overflowX: 'scroll',
            flexWrap: 'nowrap',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
                display: 'none'
            }
        }
    },
    chip: {
        height: 37,
        marginTop: 8,
        marginRight: 8,
        fontSize: 12,
        borderRadius: 100,
        background: '#fff',
        boxShadow: '0px 5px 25px rgba(92, 106, 126, 0.1);',
        '&:hover': {
            background: '#fafafa'
        },
        '&:focus': {
            background: '#fafafa'
        }
    },
    label: {
        padding: '10px 4px 7px 3px'
    },
    link: {
        textDecoration: 'none',
        color: 'initial'
    }
});

const MAX_TEMPLATES = 11;

const SearchTemplates = ({
    customClasses,
    forceShowAll,
    chipOutlined,
    onTemplateClick,
    skipDataLoading,
    skipHistoryChange,
    skipStoreChange
}) => {
    const { classes, cx } = useStyles();
    const history = useHistory();
    const [showAll, toggleShowAll] = useReducer((state) => !state, false);

    const { searchStore, sTemplatesStore } = useStores();

    useEffect(() => {
        sTemplatesStore.templates.length === 0 && sTemplatesStore.loadData();
    }, [sTemplatesStore]);

    const onChipClick = useCallback(
        (item) => () => {
            let query = item.query;
            let state = {};
            if (searchStore.type === SearchType.elastic) {
                try {
                    state = JSON.parse(item.elasticQuery);
                    query = state.searchTerm || '';
                } catch (e) {
                    console.error('[onChipClick]', e);
                }
                history.push(`/search?state=${item.elasticQuery}`);
                searchStore.setParameters({ query, type: SearchType.elastic, software: '', version: '', state });
                return searchStore.performFetch();
            }

            if (!skipStoreChange) {
                searchStore.setParameters({
                    query: item.query,
                    type: SearchType.simple,
                    software: '',
                    version: '',
                    state: {}
                });
            }
            if (!skipDataLoading) {
                searchStore.doRequest({ query: item.query });
            }
            if (!skipHistoryChange) {
                history.push(`/search?query=${item.query}`);
            }
            if (onTemplateClick) {
                onTemplateClick(item);
            }
        },
        [history, onTemplateClick, searchStore, skipDataLoading, skipHistoryChange, skipStoreChange]
    );

    const prevent = useCallback((event) => event.preventDefault(), []);

    const templates = useMemo(
        () => (showAll || forceShowAll ? sTemplatesStore.templates : sTemplatesStore.templates.slice(0, MAX_TEMPLATES)),
        [forceShowAll, sTemplatesStore.templates, showAll]
    );

    if (sTemplatesStore.templates.length === 0) {
        return null;
    }

    return (
        <div className={cx(classes.root, customClasses.root)}>
            <div className={cx(classes.chips, customClasses.chips)}>
                {templates.map((item) => (
                    <a
                        key={item.alias}
                        href={
                            searchStore.type === SearchType.elastic
                                ? `/search?state=${item.elasticQuery}`
                                : `/search?query=${item.query}`
                        }
                        className={classes.link}
                        onClick={prevent}
                    >
                        <Chip
                            key={item.alias}
                            variant={chipOutlined ? 'outlined' : 'default'}
                            className={cx(classes.chip, customClasses.chip)}
                            label={<span className={classes.label}>{item.alias}</span>}
                            onClick={onChipClick(item)}
                        />
                    </a>
                ))}

                {forceShowAll ? null : (
                    <Chip
                        key="chip-all"
                        className={cx(classes.chip, customClasses.chip)}
                        label={<span className={classes.label}>{showAll ? 'hide' : 'show all'}</span>}
                        onClick={toggleShowAll}
                    />
                )}
            </div>
        </div>
    );
};

SearchTemplates.propTypes = {
    forceShowAll: PropTypes.bool,
    chipOutlined: PropTypes.bool,
    customClasses: PropTypes.shape({
        root: PropTypes.string,
        chip: PropTypes.string,
        chips: PropTypes.string
    }),
    onTemplateClick: PropTypes.func,
    skipStoreChange: PropTypes.bool,
    skipHistoryChange: PropTypes.bool,
    skipDataLoading: PropTypes.bool
};

SearchTemplates.defaultProps = {
    customClasses: {}
};

export default observer(SearchTemplates);
